import React, { useEffect, useRef, useState } from "react";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GoldButton, Card, ui } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { BRANDING } from "@/config/branding";
import {
  UploadCloud, Inbox, FileText, Image as ImageIcon, FileSpreadsheet, Check, Trash2, Loader2,
  AlertTriangle, ExternalLink, Mail,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = "image/png,image/jpeg,image/webp,.heic,.heif,application/pdf,.csv,.xlsx,.docx";
const HIGH_CONF = 0.7;

export default function FinancialInbox() {
  const { user } = useAuth();
  const cur = user?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/documents").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/documents/categories").then((r) => setCats(r.data.categories));
  }, []);

  const rowsFromDoc = (d) =>
    (d.line_items || []).map((li) => ({
      ...li,
      selected: li.status !== "imported" && !li.is_duplicate,
      imported: li.status === "imported",
    }));

  const openReview = (d) => { setReview(d); setRows(rowsFromDoc(d)); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = data.stats?.total || 0;
      toast.success(n > 0 ? `Read ${n} transaction${n > 1 ? "s" : ""} — review & import` : "Filed in your Financial Home");
      load();
      openReview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add this document");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.item_id === id ? { ...r, ...patch } : r)));
  const selectedRows = rows.filter((r) => r.selected && !r.imported);

  const runImport = async () => {
    if (selectedRows.length === 0) return toast.error("Select at least one transaction");
    setImporting(true);
    try {
      const edits = {};
      selectedRows.forEach((r) => {
        edits[r.item_id] = { date: r.date, description: r.description, amount: Number(r.amount) || 0, type: r.type, category: r.category };
      });
      const { data } = await api.post(`/documents/${review.id}/import`, {
        item_ids: selectedRows.map((r) => r.item_id), edits,
      });
      toast.success(`${data.imported} transaction${data.imported === 1 ? "" : "s"} added to Cash Flow`);
      setReview(null); load();
    } catch { toast.error("Could not import"); }
    finally { setImporting(false); }
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); toast.success("Removed"); setReview(null); load(); };

  const bulkImport = async (d, e) => {
    if (e) e.stopPropagation();
    setBulkBusy(d.id);
    try {
      const { data } = await api.post(`/documents/${d.id}/import`, {});
      setSummary({
        title: d.title,
        total: data.total, imported: data.imported,
        duplicates: data.skipped_duplicates, needs_review: data.skipped_needs_review,
        failed: data.skipped_other,
      });
      load();
    } catch { toast.error("Could not import"); }
    finally { setBulkBusy(null); }
  };

  const canBulk = (d) => d.status === "pending" && (d.confidence || 0) >= HIGH_CONF && (d.stats?.new || 0) > 0;

  const pending = items.filter((i) => i.status === "pending").length;
  const docIcon = (d) => (d.kind === "image" ? ImageIcon : (d.kind === "csv" || d.kind === "xlsx") ? FileSpreadsheet : FileText);
  const multi = review && (review.stats?.total || 0) > 1;

  return (
    <div>
      <PageHeader title="Financial Inbox" subtitle="Email or upload receipts, bills & statements — we read them and turn them into transactions."
        action={
          <GoldButton data-testid="inbox-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "Reading…" : "Add to Your Financial Home"}
          </GoldButton>
        } />

      {BRANDING.inboxEmail && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-sm text-[#6b6256] bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-2.5 w-fit">
          <Mail size={15} className="text-[#C6A45C]" /> Forward documents to
          <span className="font-mono-data text-[#8a6d2f]" data-testid="inbox-alias">{BRANDING.inboxEmail}</span>
          <span className="text-xs text-[#A99F90]">— send from your account email and they'll appear here automatically.</span>
        </div>
      )}

      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="inbox-file-input" />

      {items.length === 0 && !uploading ? (
        <button data-testid="inbox-dropzone" onClick={() => fileRef.current?.click()}
          className="w-full border-2 border-dashed border-[#D9CDBA] rounded-2xl p-14 flex flex-col items-center justify-center text-[#8A7F70] hover:border-[#C6A45C] hover:bg-[#C6A45C]/5 transition-all bg-white/40">
          <div className="w-14 h-14 rounded-full bg-[#F0E7D8] flex items-center justify-center mb-4">
            <UploadCloud size={26} className="text-[#C6A45C]" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl text-[#2C2A28]">Add your first document</div>
          <div className="text-sm mt-2 max-w-md">Receipts, invoices, bills, PDF or CSV bank statements, spreadsheets, insurance, mortgage and tax documents — we read every line and turn it into Cash Flow.</div>
        </button>
      ) : (
        <>
          {pending > 0 && (
            <div className="mb-4 text-sm text-[#8a6d2f] bg-[#C6A45C]/10 border border-[#C6A45C]/25 rounded-xl px-4 py-3 inline-flex items-center gap-2">
              {pending} document{pending > 1 ? "s" : ""} awaiting your quick review.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((d, i) => {
              const Icon = docIcon(d);
              const total = d.stats?.total || 0;
              return (
                <Card key={d.id} className="fade-up cursor-pointer hover:-translate-y-0.5 transition-transform" style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`doc-card-${d.id}`} onClick={() => openReview(d)}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F0E7D8] flex items-center justify-center"><Icon size={18} className="text-[#C6A45C]" /></div>
                    {d.status === "imported"
                      ? <Badge className="bg-[#8FB996]/15 text-[#5a9a6a] border-[#8FB996]/30"><Check size={12} className="mr-1" /> Imported</Badge>
                      : d.status === "stored"
                        ? <Badge className="bg-[#EAE0D2] text-[#8A7F70] border-0">Stored</Badge>
                        : <Badge className="bg-[#C6A45C]/15 text-[#8a6d2f] border-[#C6A45C]/30">Review</Badge>}
                  </div>
                  <div className="font-display text-base text-[#2C2A28] line-clamp-1">{d.title}</div>
                  <div className="text-xs text-[#A99F90] mt-1">{d.doc_type}{d.upload_method === "email" ? " · emailed" : ""}</div>
                  {total > 0 && (
                    <div className="text-xs text-[#6b6256] mt-2">
                      {total} transaction{total > 1 ? "s" : ""}
                      {d.stats?.duplicates > 0 ? ` · ${d.stats.duplicates} duplicate${d.stats.duplicates > 1 ? "s" : ""}` : ""}
                    </div>
                  )}
                  {canBulk(d) && (
                    <button data-testid={`bulk-import-${d.id}`} onClick={(e) => bulkImport(d, e)} disabled={bulkBusy === d.id}
                      className="mt-3 w-full flex items-center justify-center gap-2 text-sm text-[#8a6d2f] border border-[#C6A45C]/40 bg-[#C6A45C]/8 rounded-xl px-3 py-2 hover:bg-[#C6A45C]/15 transition-all disabled:opacity-60">
                      {bulkBusy === d.id ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Import all new ({d.stats.new})</>}
                    </button>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className={`${ui.dialog} max-w-3xl`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">{review?.title || "Review"}</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">
              {review?.doc_type} · review the extracted data, then import to Cash Flow.
            </DialogDescription>
          </DialogHeader>
          {review && (
            <div className="space-y-4 py-1">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                {review.stats?.total > 0 && (
                  <span data-testid="review-stats" className="text-[#6b6256]">
                    <b className="text-[#2C2A28]">{review.stats.total}</b> found ·{" "}
                    <b className="text-[#5a9a6a]">{review.stats.new}</b> new ·{" "}
                    <b className="text-[#b06b6b]">{review.stats.duplicates}</b> duplicate{review.stats.duplicates === 1 ? "" : "s"}
                  </span>
                )}
                {review.confidence ? (
                  <span data-testid="doc-confidence" className="flex items-center gap-1.5 text-[#A99F90]">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: review.confidence >= 0.7 ? "#8FB996" : review.confidence >= 0.4 ? "#C6A45C" : "#C99FA0" }} />
                    AI confidence {Math.round(review.confidence * 100)}%
                  </span>
                ) : null}
                {review.storage_path && (
                  <a data-testid="doc-view-source" href={`${BACKEND}/api/documents/${review.id}/file`} target="_blank" rel="noreferrer"
                    className="ml-auto flex items-center gap-1.5 text-[#8a6d2f] hover:underline"><ExternalLink size={14} /> View source document</a>
                )}
              </div>

              {review.summary && <p className="text-sm text-[#6b6256] bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-2.5">{review.summary}</p>}
              {review.parse_error && rows.length === 0 && (
                <p className="text-sm text-[#b06b6b] bg-[#C99FA0]/10 border border-[#C99FA0]/30 rounded-xl px-4 py-2.5 flex items-center gap-2">
                  <AlertTriangle size={15} /> We couldn't read this automatically. You can still keep it on file as a source document.
                </p>
              )}

              {rows.length > 0 && (
                <div className="max-h-[44vh] overflow-y-auto rounded-xl border border-[#EAE0D2]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#FBF7F1]">
                      <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#8A7F70] border-b border-[#EAE0D2]">
                        <th className="px-2 py-2 w-8"></th>
                        <th className="px-2 py-2">Date</th>
                        <th className="px-2 py-2">Description</th>
                        <th className="px-2 py-2">Category</th>
                        <th className="px-2 py-2">Type</th>
                        <th className="px-2 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.item_id} data-testid={`review-row-${r.item_id}`}
                          className={`border-b border-[#F0E9DD] ${r.imported ? "opacity-50" : ""} ${r.is_duplicate ? "bg-[#C99FA0]/5" : ""}`}>
                          <td className="px-2 py-1.5">
                            <Checkbox data-testid={`review-check-${r.item_id}`} checked={r.selected} disabled={r.imported}
                              onCheckedChange={(v) => setRow(r.item_id, { selected: !!v })}
                              className="border-[#D9CDBA] data-[state=checked]:bg-[#C6A45C] data-[state=checked]:border-[#C6A45C]" />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="date" value={r.date || ""} onChange={(e) => setRow(r.item_id, { date: e.target.value })}
                              className="h-8 text-xs bg-white/70 border-[#E4DACB]" />
                          </td>
                          <td className="px-2 py-1.5 min-w-[160px]">
                            <Input value={r.description || ""} onChange={(e) => setRow(r.item_id, { description: e.target.value })}
                              className="h-8 text-xs bg-white/70 border-[#E4DACB]" />
                            {r.is_duplicate && <span className="text-[10px] text-[#b06b6b] flex items-center gap-1 mt-0.5"><AlertTriangle size={10} /> already in Cash Flow</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={r.category} onValueChange={(v) => setRow(r.item_id, { category: v })}>
                              <SelectTrigger className="h-8 text-xs bg-white/70 border-[#E4DACB] w-[130px]"><SelectValue /></SelectTrigger>
                              <SelectContent className={ui.selectContent}>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Select value={r.type} onValueChange={(v) => setRow(r.item_id, { type: v })}>
                              <SelectTrigger className="h-8 text-xs bg-white/70 border-[#E4DACB] w-[100px]"><SelectValue /></SelectTrigger>
                              <SelectContent className={ui.selectContent}>
                                <SelectItem value="expense">Expense</SelectItem>
                                <SelectItem value="income">Income</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" value={r.amount} onChange={(e) => setRow(r.item_id, { amount: e.target.value })}
                              className="h-8 text-xs bg-white/70 border-[#E4DACB] w-[90px] text-right" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2 items-center">
            <button data-testid="doc-delete-btn" onClick={() => remove(review.id)} className="text-[#b06b6b] hover:bg-[#C99FA0]/10 px-4 py-2.5 rounded-full text-sm flex items-center gap-2"><Trash2 size={15} /> Remove</button>
            {rows.length > 0
              ? <GoldButton data-testid="doc-import-btn" onClick={runImport} disabled={importing} className="flex-1">
                  {importing ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} /> Import {selectedRows.length} to Cash Flow</>}
                </GoldButton>
              : <GoldButton data-testid="doc-keep-btn" onClick={() => setReview(null)} className="flex-1">Keep on file</GoldButton>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent className={ui.dialog}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">Import complete</DialogTitle>
            <DialogDescription className="text-[#8A7F70]">{summary?.title}</DialogDescription>
          </DialogHeader>
          {summary && (
            <div className="space-y-2 py-2" data-testid="import-summary">
              {[
                ["Total rows detected", summary.total, "#6b6256"],
                ["New transactions imported", summary.imported, "#5a9a6a"],
                ["Duplicates skipped", summary.duplicates, "#b06b6b"],
                ["Rows needing review", summary.needs_review, "#8a6d2f"],
                ["Rows failed", summary.failed, "#A99F90"],
              ].map(([label, val, color]) => (
                <div key={label} className="flex items-center justify-between bg-white/60 border border-[#EAE0D2] rounded-xl px-4 py-2.5">
                  <span className="text-sm text-[#6b6256]">{label}</span>
                  <span className="font-mono-data text-lg" style={{ color }}>{val}</span>
                </div>
              ))}
              {(summary.needs_review > 0 || summary.duplicates > 0) && (
                <p className="text-xs text-[#A99F90] pt-1">Skipped rows remain in the document — open it to review or edit them.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <GoldButton data-testid="import-summary-close" onClick={() => setSummary(null)} className="w-full">Done</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DisclaimerFooter className="mt-8 border-t border-[#EAE0D2] pt-5" />
    </div>
  );
}
